import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {  Features } from '../../../models/models';
import { 
  IFeaturesService,
  IReportService,
} from '../../../services/services';
import { Title } from '@angular/platform-browser';
import { ICurrentUserService } from '../../../services/currentUserService/icurrentuser.service';
import { ToastMessageOptions, SelectItem } from 'primeng/api';
import { Company, CompanyClient, DeviceClient, IVendorJobClient, VendorJob } from '../../../services/nswag/nswagclient';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'post-job-analysis',
  
  standalone: false,
  templateUrl: './post-job-analysis.component.html',
  styleUrls: ['./post-job-analysis.component.css']
})
export class PostJobAnalysisComponent implements OnInit {
  public cols: any[] = [];
  public jobs: VendorJob[] = [];
  public originalJobs: VendorJob[] = [];
  public jobsReady = false;
  public selectedJobs: VendorJob[] = [];
  public companies: Company[] = [];
  public companiesReady = false;
  public devicesReady = false;
  public deviceNamesSelection: SelectItem[] = [];
  public everythingReady = false;
  public featuresReady = false;
  public postJobFeatureEnabled = false;
  public securityToken = '';
  public clearString: string = "Clear";
  public calendarEndTime: Date = new Date();
  public calendarStartTime: Date = new Date(this.calendarEndTime.getFullYear(), 0, 1);
  messages: ToastMessageOptions[] = [];

  constructor(
    private _companyService: CompanyClient,
    private _deviceService: DeviceClient,
    private _featuresService: IFeaturesService,
    @Inject("IVendorJobClient") private _jobService: IVendorJobClient,
    private _reportService: IReportService,
    private _titleService: Title,
    private _router: Router,
    private _currentUserService: ICurrentUserService
  ) { }

  ngOnInit() {
    this.setPageTitle();
    this.setColumns();
    this.getJobs();
    this.getFeatures();
    this.setSecurityToken();
  }
  setSecurityToken(){

    this._currentUserService.getAccessToken().subscribe(s=>{
      this.securityToken = s ?? '';
    });
  }
  setPageTitle() {
    this._titleService.setTitle('OpsViewer Post Job Analysis');
  }

  setColumns() {
    this.cols = [
      {field: 'name', header: 'Name', width: '200px', filterMatchMode: 'contains'},
      {field: 'company.name', header: 'Company', width: '200px', filterMatchMode: 'contains'},
      {field: 'startTime', header: 'Start', width: '150px'},
      {field: 'endTime', header: 'End', width: '150px'},
      {field: 'devices', header: 'Devices', width: '200px', filterMatchMode: 'contains', alternativeFilterField: 'deviceNamesAsString'},
    ];
  }

  getJobs() {
    this.messages = [];
    this._jobService.getVendorJobs().subscribe(
      results => {
        this.originalJobs = results;
        this.searchJobs();
        this.getCompanies();
        this.getDevices();
        this.jobsReady = true;
        this.checkIfEverythingReady();
      },
      error => {
        const detail = error.error ? error.error.Message : error.message;
        this.messages = [];
        this.messages.push({
          severity: 'error',
          summary: 'Could not get Jobs',
          detail: detail
        });
        console.error(error);
      }
    );
  }

  getCompanies() {
    this._companyService.get().subscribe(
      results => {
        this.companies = results;
        results.forEach(result => {
          const jobs = this.jobs.filter(job => job.companyId === result.id) as any[];
          jobs.forEach(job => { job.company = result; });
        });
        this.companiesReady = true;
        this.checkIfEverythingReady();
      },
      error => {
        this.messages = [];
        this.messages.push({
          severity: 'error',
          summary: 'Could not get list of companies',
          detail: ''
        });
        console.error(error);
      }
    );
  }

  getDevices() {
  this._deviceService.get(false).subscribe(
      results => {
        this.deviceNamesSelection = [];
        results.forEach(result => {
          const resultId = result.id;
          if (!resultId) {
            return;
          }

          const resultName = result.name ?? '';
          this.deviceNamesSelection.push({ label: resultName, value: resultId });
          const jobs = this.jobs.filter(j => j.deviceList?.includes(resultId) ) as any[];
          jobs.forEach(job => {
            if (!job.devices) { job.devices = []; }
            const deviceToAdd = { label: resultName, value: resultId };
            if (!job.devices.includes(deviceToAdd)) { job.devices.push(deviceToAdd); }
            if (!job.deviceNamesAsString) {
              job.deviceNamesAsString = deviceToAdd.label;
            } else {
              job.deviceNamesAsString = job.deviceNamesAsString + ', ' + deviceToAdd.label;
            }
          });
        });
        this.devicesReady = true;
        this.checkIfEverythingReady();
      },
      error => {
        this.messages = [];
        this.messages.push({
          severity: 'error',
          summary: 'Could not get Companies',
          detail: ''
        });
        console.error(error);
      }
    );
  }

  getFeatures() {
    this._featuresService.isFeatureEnabled(Features.PostJobAnalysis).subscribe(
      result => {
        this.postJobFeatureEnabled = this.featuresReady = result;
        if (!this.postJobFeatureEnabled)
          this._router.navigate(['/']);
        this.checkIfEverythingReady();
     },
     error => {
      this.messages = [];
      this.messages.push({
        severity: 'error',
        summary: 'Could not get Features',
        detail: ''
      });
      console.error(error);
     }
   );
  }

  checkIfEverythingReady() {
    this.everythingReady = this.jobsReady && this.companiesReady && this.devicesReady && this.featuresReady;
  }

  clearSelection(){
    this.selectedJobs = [];
  }

  runAnalysis(){
    const jobIds: string[] = [];
    this.selectedJobs.forEach(j => {
      if (j.id) {
        jobIds.push(j.id);
      }
    });
    this._reportService.getPostAnalysisReport(jobIds).subscribe(
      data => {
        const blob = new Blob([data], {type:'application/vnd.ms-excel'});
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      }
    );
  }

  searchJobs(){
    this.jobs = this.originalJobs.filter((j) => {
      if (!j.startTime) {
        return false;
      }

      const startTime = new Date(j.startTime);
      const endTime = j.endTime ? new Date(j.endTime) : null;
      return startTime >= this.calendarStartTime && (endTime == null || endTime <= this.calendarEndTime);
    });
  }

  updateClearString(){
    this.clearString = `Clear (${this.selectedJobs.length})`;
  }

  toMessageSeverity(severity: string | undefined): 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast' | null | undefined {
    switch (severity) {
      case 'success':
      case 'info':
      case 'warn':
      case 'error':
      case 'secondary':
      case 'contrast':
        return severity;
      default:
        return undefined;
    }
  }
}

